/**
 * Every gamification / product number in one place.
 *
 * "confirmed" = the value is fixed by the reference app's own copy or validation messages
 * (see docs/01-APK-ANALYSIS.md). "default" = the reference keeps the value in downloadable config,
 * so we use the analysis' proposed default and tune it in playtests.
 */

/* ------------------------------- Sense of progress ------------------------------- */

/** Cumulative sparks needed for level 1..7 (default; must start at 0 and strictly increase). Levels 1-7 confirmed. */
/** Curriculum v2 (decision 2026-10-18): the v1 thresholds [0, 30, 80, 150, 250, 400, 600] x 2.5 to match ~1 830 path sparks. */
export const LEVEL_THRESHOLDS = [0, 75, 200, 375, 625, 1000, 1500] as const;
export const MAX_LEVEL = LEVEL_THRESHOLDS.length; // 7

/** Sparks per content type (default: 10 per completed drawing). */
export const SPARKS_BY_CONTENT_TYPE = {
  pathLesson: 10,
  playground: 10,
  studio: 15,
  drawTogetherHost: 10,
  drawTogetherPartner: 10,
  challengeReward: 20,
  skillJourneyStep: 5,
  journalUpload: 0,
} as const;
export type SparkContentType = keyof typeof SPARKS_BY_CONTENT_TYPE;

/** Curriculum v2: sparks per activity type (docs/CURRICULUM-V2.md §A.4). */
export const SPARKS_BY_ACTIVITY = {
  doodle: 5,
  core: 10,
  remix: 10,
  colouring: 10,
  scene: 15,
  masterpiece: 30,
  thenNow: 15,
  challenge: 15,
  observe: 10,
  crossover: 20,
  picture: 20,
} as const;

/** Bonus features unlocked by total sparks (default thresholds; the feature list is confirmed). */
export const BONUS_FEATURES = [
  { id: 'journal', sparks: 20 },
  { id: 'tips', sparks: 30 },
  { id: 'letters', sparks: 60 },
  { id: 'stories', sparks: 80 },
  { id: 'studio', sparks: 150 },
  { id: 'referenceLibrary', sparks: 250 },
] as const;
export type BonusFeatureId = (typeof BONUS_FEATURES)[number]['id'];

/** Stickers available at each level (24 in total: 4 from the start, then 5 then 3 per level; "5 / 3 new stickers" confirmed). */
export const STICKERS_PER_LEVEL: Record<number, number> = { 1: 4, 2: 5, 3: 3, 4: 3, 5: 3, 6: 3, 7: 3 };

/* ---------------------------------- Skills ---------------------------------- */

/** Skill XP needed for skill level 1..4 (default). */
export const SKILL_LEVEL_THRESHOLDS = [0, 30, 80, 150] as const;
/** XP awarded to each skill a lesson teaches (default). */
export const SKILL_XP_PER_LESSON = 10;
/** XP awarded to a skill for the first capture of each of its journey steps (default; sparks: SPARKS_BY_CONTENT_TYPE.skillJourneyStep). */
export const SKILL_XP_PER_JOURNEY_STEP = 10;
/** Photos kept per journey step (re-captures replace the oldest). */
export const SKILL_JOURNEY_CAPTURES_PER_STEP = 5;

/* --------------------------------- FTUE / path -------------------------------- */

/** "Draw two to unlock" (confirmed copy): drawings needed in the first stage before Learn opens. */
export const FTUE_DRAWINGS_TO_UNLOCK_LEARN = 2;
/** Intro coin = outline, pattern, shading pieces (confirmed: 3). */
export const COIN_PIECES = ['outline', 'pattern', 'shading'] as const;
/** Levels (units) per path (default 4: "Level 1..4" confirmed in copy). */
export const PATH_LEVELS = 4;

/* --------------------------------- Accounts --------------------------------- */

export const MAX_PROFILES = 5; // confirmed "You can create up to 5 profiles"
export const INDIVIDUAL_FULL_ACCESS_PROFILES = 1; // confirmed "Full access for 1 profile"
export const MIN_AGE = 4; // confirmed "age must be between 4 and 99"
export const MAX_AGE = 99;
export const VERIFICATION_CODE_LENGTH = 4; // confirmed "4-digit code"
export const ADULT_AGE = 18;

/* -------------------------------- Monetisation ------------------------------- */

/** Free trial length (days). Decision 2026-10-18: yearly packages only. */
export const TRIAL_DAYS = 7; // confirmed
/** Trial for a family that claimed an invite (referral offering `invited_14d`, yearly only). */
export const TRIAL_DAYS_INVITED = 14;
export const TRIAL_REMINDER_DAY = 5; // confirmed "Day 5: Get a trial reminder"
/** Days before the first charge at which the parent is reminded (day 5 of a 7-day trial = 3 days before). */
export const TRIAL_REMINDER_DAYS_BEFORE_CHARGE = TRIAL_DAYS - TRIAL_REMINDER_DAY + 1;
/**
 * US / dev fallback prices (USD cents). The paywall shows store prices (RevenueCat / Stripe) or the
 * regional table in `pricing.ts`; never these constants directly. Plus = product id `individual`.
 */
export const PRICES_USD_CENTS = {
  individual: { monthly: 999, yearly: 5999 },
  family: { monthly: 1299, yearly: 7999 },
} as const;
export type PlanId = keyof typeof PRICES_USD_CENTS;
export type BillingPeriod = 'monthly' | 'yearly';

/* --------------------------------- Retention -------------------------------- */

export const WEEKLY_CHALLENGE_TARGET = 5; // confirmed "Draw 5 ... to complete the challenge"
export const WEEKLY_CHALLENGE_DAYS = 7;
/** Monday the weekly schedule is anchored to. */
export const WEEKLY_CHALLENGE_ANCHOR = '2026-01-05T00:00:00.000Z';

export const LETTER_DRAWINGS = 5; // confirmed "Select 5 drawings to send"
export const ENVELOPE_DRAWINGS_TO_FILL = 5; // default
export const LETTER_DELIVERY_HOURS = 24; // default
export const LETTER_MILESTONES = 4; // confirmed milestones 1-4
export const LETTER_VARIANTS = 3; // confirmed variants 1-3

export const STORY_MIN_DAYS_BETWEEN = 7; // default: one episode per week after unlock

export const VOICE_SURVEY_MAX_SECONDS = 60; // confirmed
/** Upload cap for one voice clip (60 s of compressed speech is ~0.5 MB). */
export const VOICE_SURVEY_MAX_BYTES = 2 * 1024 * 1024;
export const JOURNAL_TITLE_MAX = 40; // default
export const JOURNAL_DESCRIPTION_MAX = 240; // default
export const JOURNAL_STICKERS_PER_PAGE = 6; // default
export const REVIEW_MIN_SAVED_DRAWINGS = 3; // default trigger_drawing_count

/* -------------------------------- Recognition -------------------------------- */

export const RECOGNITION_NETWORK_DEADLINE_MS = 15_000; // default
export const RECOGNITION_FEEDBACK_DEADLINE_MS = 30_000; // default
